import axios from 'axios';
import { ReplaySubject } from 'rxjs';
import { Logo, LogoCreateInput, LogoUpdateInput, MultipleOccurenceEventInput, Profile, Room, RoomCreateInput, RoomUpdateInput, ScreenEventInput, ShortURLCreateInput, ShortURLUpdateInput, UserInfo } from './interfaces';
import { getCookie } from './utils';
import { take } from 'rxjs/operators';

let rooms: Room[] | undefined;

export async function getAvailableRooms(refetch = false) {
    if (!rooms || refetch) {
        rooms = (await axios.get<Room[]>('/screen-events/api/rooms/')).data;
    }
    return rooms
}

let logos: Logo[] | undefined;
export async function getAvailableLogos(refetch = false) {
    if (!logos || refetch) {
        const response = await axios.get<Logo[]>('/screen-events/api/logos/');
        logos = response.data;
    }
    return logos;
}

function mapEventToInput(input: ScreenEventInput) {
    return {
        title: input.title,
        date: input.date,
        tstart: input.tstart,
        tend: input.tend,
        'logo_id': input.logo?.id || null,
        'room_id': input.room?.id || null
    };
}

function getHeaders(): { [key: string]: string | undefined } {
    const csrftoken = getCookie('csrftoken');
    return {
        'X-CSRFToken': csrftoken
    };
}

export async function login(username: string, password: string) {
    try {
        const response = await axios.post('/login', { username, password }, {
            headers: getHeaders()
        });
        return response.status === 200;
    } catch (e: any) {
        if (e?.response?.status === 401) {
            return false;
        }
        throw e;
    }
}

export async function createNewEvent(input: ScreenEventInput) {
    const response = await axios.post('/screen-events/api/events/', mapEventToInput(input), {
        headers: getHeaders()
    });
    return response.status === 201;
}

export async function createMultipleOccurenceEvent(input: MultipleOccurenceEventInput): Promise<boolean> {
    const response = await axios.post('/screen-events/api/events/multi_create/', input, { headers: getHeaders() });
    return response.status === 201;
}

export async function updateEvent(input: ScreenEventInput & { id: number }) {
    const response = await axios.put(`/screen-events/api/events/${input.id}/`, mapEventToInput(input), {
        headers: getHeaders()
    });
    return response.status === 200;
}

export async function createLogo(input: LogoCreateInput) {
    const headers = getHeaders();
    const formData = new FormData();
    formData.append('title', input.title);
    formData.append('image', input.image);
    headers['Content-Type'] = 'multipart/form-data';
    const response = await axios.post(`/screen-events/api/logos/`, formData, {
        headers,

    });
    return response.status === 201;
}

export async function updateLogo(input: LogoUpdateInput) {

    const headers = getHeaders();
    const formData = new FormData();
    formData.append('title', input.title);
    if (input.image) {
        formData.append('image', input.image);
    }
    headers['Content-Type'] = 'multipart/form-data';
    const response = await axios.put(`/screen-events/api/logos/${input.id}/`, formData, {
        headers
    });
    return response.status === 200;
}

export async function createRoom(input: RoomCreateInput) {
    const response = await axios.post('/screen-events/api/rooms/', input, {
        headers: getHeaders()
    });
    return response.status === 201;
}

export async function updateRoom(input: RoomUpdateInput) {
    const response = await axios.put(`/screen-events/api/rooms/${input.id}/`, input, {
        headers: getHeaders()
    });
    return response.status === 200;
}

export async function deleteRoom(id: number) {
    const response = await axios.delete(`/screen-events/api/rooms/${id}/`, {
        headers: getHeaders()
    });
    return response.status === 204;
}

export async function deleteEvent(id: number) {
    const response = await axios.delete(`/screen-events/api/events/${id}/`, {
        headers: getHeaders()
    });
    return response.status === 204;
}

const profile = new ReplaySubject<UserInfo | undefined>(1);

axios.get<UserInfo>('/screen-events/profile/')
    .then(res => {
        if (!Object.keys(res.data).length) {
            profile.next(undefined);
        } else {
            profile.next(res.data);
        }
    }).catch(() => profile.next(undefined));

export async function getProfile() {
    return profile.pipe(take(1)).toPromise();
}


export async function deleteURL(id: number) {
    const response = await axios.delete(`/api/urls/${id}/`, {
        headers: getHeaders()
    });
    return response.status === 204;
}

export async function createURL(input: ShortURLCreateInput): Promise<OperationResponse<any>> {
    const response = await axios.post('/api/urls/', input, {
        headers: getHeaders(),
        validateStatus: (status) => status === 201 || status === 400
    });
    return { success: response.status === 201, response: response.data };
}

export interface OperationResponse<T> {
    success: boolean;
    response: T;
}

export async function updateURL(input: ShortURLUpdateInput): Promise<OperationResponse<any>> {
    const response = await axios.put(`/api/urls/${input.id}/`, input, {
        headers: getHeaders(),
        validateStatus: (status) => status === 200 || status === 400
    });
    return { success: response.status === 200, response: response.data };
}

getProfile().then().catch();


export interface OnlineDonation {
    id: number;
    created: string;
    amount: number;
    payment_method: number;
    purpose: string;
    email: string;
    repetition: number;
    iban: string;
    first_name: string;
    last_name: string;
    street: string;
    postcode: string;
    city: string;
}

export async function getDonations(): Promise<ReadonlyArray<OnlineDonation>> {
    return (await axios.get<ReadonlyArray<OnlineDonation>>('/api/donations/')).data;
}

export async function confirmDonation(id: number): Promise<boolean> {
    const response = await axios.post(`/api/donations/${id}/confirm/`, undefined, { headers: getHeaders() });
    return response.status === 200;
}


export interface SolarStats {
    today: { date: string; value: number }[];
    yesterday: { date: string; value: number }[];
    "month": { "total_energy__sum": number };
    "year": { "total_energy__sum": number };
    "max_day": number;
    "total_energy": number;
    'last_days': { "total_energy": number; "day": string }[];
    "yearly": { "total_energy": number; "day__month": number }[];
}

export async function fetchSolarStats(): Promise<SolarStats> {
    const response = await axios.get('/stats', {
        headers: getHeaders()
    });
    return response.data;
}